import { useState } from 'react';
import {
  formatearCLP,
  type EstadoProyecto,
  type ModalidadMonto,
  type ModoFacturacion,
} from '@lexicore/shared';
import { IconoCerrar, IconoMas } from '../../componentes/Iconos';
import { useAvisos } from '../../contextos/AvisosContext';
import {
  useConfiguracionEmpresa,
  useOrganismos,
  useUsuariosEmpresa,
} from '../../lib/consultas';
import { useCrearProyecto } from '../../lib/mutaciones';

interface Props {
  alCerrar: () => void;
  alCrearExitoso?: (nuevoId?: string) => void;
}

export function ModalCrearProyecto({ alCerrar, alCrearExitoso }: Props) {
  const avisos = useAvisos();
  const configQuery = useConfiguracionEmpresa();
  const organismosQuery = useOrganismos();
  const usuariosQuery = useUsuariosEmpresa();
  const crearMut = useCrearProyecto();

  const hoyStr = new Date().toISOString().slice(0, 10);
  const ivaPct = configQuery.data?.ivaPct ?? 19;
  const factorIva = 1 + ivaPct / 100;

  // ── Estado del formulario ──
  const [nombre, setNombre] = useState('');
  const [descripcion, setDescripcion] = useState('');
  const [organismoId, setOrganismoId] = useState('');
  const [responsableId, setResponsableId] = useState('');

  const [modalidadMonto, setModalidadMonto] = useState<ModalidadMonto>('BRUTO');
  const [montoContratoNeto, setMontoContratoNeto] = useState<number>(0);
  const [montoContratoBruto, setMontoContratoBruto] = useState<number>(0);

  const [presupuestoCostoNeto, setPresupuestoCostoNeto] = useState<number>(0);
  const [fechaInicio, setFechaInicio] = useState(hoyStr);
  const [fechaFinPlanificada, setFechaFinPlanificada] = useState('');

  const [modoFacturacion, setModoFacturacion] = useState<ModoFacturacion>('AL_CIERRE');
  const [numeroOrdenCompra, setNumeroOrdenCompra] = useState('');
  const [fechaOrdenCompra, setFechaOrdenCompra] = useState('');
  const [notas, setNotas] = useState('');

  // Sincronización automática de montos tributarios
  const manejarCambioNeto = (valor: number) => {
    setMontoContratoNeto(valor);
    setMontoContratoBruto(Math.round(valor * factorIva));
  };

  const manejarCambioBruto = (valor: number) => {
    setMontoContratoBruto(valor);
    setMontoContratoNeto(Math.round(valor / factorIva));
  };

  const guardar = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!nombre.trim()) {
      avisos.error('El nombre del proyecto es obligatorio.');
      return;
    }
    if (montoContratoNeto <= 0) {
      avisos.error('El monto contractual debe ser mayor a $0.');
      return;
    }
    if (!fechaInicio) {
      avisos.error('La fecha de inicio es requerida.');
      return;
    }

    try {
      const res = await crearMut.mutateAsync({
        nombre: nombre.trim(),
        descripcion: descripcion.trim() || undefined,
        organismoId: organismoId || null,
        responsableId: responsableId || null,
        estado: 'PLANIFICADO' as EstadoProyecto,
        modalidadMonto,
        montoContratoNeto: Math.round(montoContratoNeto),
        montoContratoBruto: Math.round(montoContratoBruto),
        presupuestoCostoPlanificado: Math.round(presupuestoCostoNeto),
        presupuestoCostoPlanificadoBruto: Math.round(presupuestoCostoNeto * factorIva),
        fechaInicio,
        fechaFinPlanificada: fechaFinPlanificada || undefined,
        modoFacturacion,
        numeroOrdenCompra: numeroOrdenCompra.trim() || undefined,
        fechaOrdenCompra: fechaOrdenCompra || undefined,
        notas: notas.trim() || undefined,
      });

      avisos.exito('Proyecto creado exitosamente en estado Planificado.');
      if (alCrearExitoso) alCrearExitoso(res.id);
      alCerrar();
    } catch (err) {
      avisos.error(err instanceof Error ? err.message : 'No se pudo crear el proyecto.');
    }
  };

  const margenEstimadoNeto = montoContratoNeto - presupuestoCostoNeto;
  const margenEstimadoPct =
    montoContratoNeto > 0 ? Math.round((margenEstimadoNeto / montoContratoNeto) * 100) : 0;

  return (
    <div
      className="modal-overlay"
      onClick={(e) => e.target === e.currentTarget && alCerrar()}
      role="dialog"
      aria-modal="true"
    >
      <div className="modal" style={{ maxWidth: 820, maxHeight: '90vh', overflowY: 'auto' }}>
        <div className="modal-header">
          <div>
            <h2 style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <IconoMas size={18} />
              <span>Nuevo Proyecto</span>
            </h2>
            <p style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2 }}>
              Registra un nuevo contrato u obra para control operativo, presupuesto y conciliación de cobranza.
            </p>
          </div>
          <button
            type="button"
            className="btn-icono"
            onClick={alCerrar}
            aria-label="Cerrar modal"
          >
            <IconoCerrar size={14} />
          </button>
        </div>

        <form onSubmit={guardar}>
          <div className="modal-body" style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
            {/* 1. Datos Principales */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 16 }}>
              <div className="form-group" style={{ gridColumn: '1 / -1' }}>
                <label htmlFor="p_nombre">Nombre del Proyecto *</label>
                <input
                  id="p_nombre"
                  type="text"
                  value={nombre}
                  onChange={(e) => setNombre(e.target.value)}
                  placeholder="Ej: Modernización Infraestructura Red Hospital Regional"
                  required
                  autoFocus
                />
              </div>

              <div className="form-group">
                <label htmlFor="p_organismo">Cliente / Organismo Público</label>
                <select
                  id="p_organismo"
                  value={organismoId}
                  onChange={(e) => setOrganismoId(e.target.value)}
                >
                  <option value="">Seleccionar cliente u organismo…</option>
                  {organismosQuery.data?.map((org) => (
                    <option key={org.id} value={org.id}>
                      {org.nombre} {org.rut ? `(${org.rut})` : ''}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="p_responsable">Responsable Asignado</label>
                <select
                  id="p_responsable"
                  value={responsableId}
                  onChange={(e) => setResponsableId(e.target.value)}
                >
                  <option value="">Sin responsable asignado</option>
                  {usuariosQuery.data?.map((u) => (
                    <option key={u.id} value={u.id}>
                      {u.nombre} ({u.rol})
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* 2. Cifras Contractuales & Presupuesto */}
            <div
              style={{
                background: 'var(--bg-hover)',
                border: '1px solid var(--border-subtle)',
                borderRadius: 'var(--radius-md)',
                padding: 16,
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                <span style={{ fontWeight: 700, fontSize: 13, color: 'var(--text-primary)' }}>
                  Montos Contractuales y Rentabilidad Planificada
                </span>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12 }}>
                  <span style={{ color: 'var(--text-muted)' }}>Modalidad:</span>
                  <button
                    type="button"
                    className={`btn btn-sm ${modalidadMonto === 'BRUTO' ? 'btn-primary' : 'btn-secondary'}`}
                    style={{ padding: '2px 8px', fontSize: 11 }}
                    onClick={() => setModalidadMonto('BRUTO')}
                  >
                    Bruto (con IVA)
                  </button>
                  <button
                    type="button"
                    className={`btn btn-sm ${modalidadMonto === 'NETO' ? 'btn-primary' : 'btn-secondary'}`}
                    style={{ padding: '2px 8px', fontSize: 11 }}
                    onClick={() => setModalidadMonto('NETO')}
                  >
                    Neto
                  </button>
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 14 }}>
                <div className="form-group">
                  <label htmlFor="p_monto_neto">Monto Contrato Neto (CLP) *</label>
                  <input
                    id="p_monto_neto"
                    type="number"
                    min={0}
                    step={1000}
                    value={montoContratoNeto || ''}
                    onChange={(e) => manejarCambioNeto(Number(e.target.value))}
                    placeholder="Ej: 25000000"
                    required
                  />
                  <small style={{ fontSize: 10.5, color: 'var(--text-muted)' }}>
                    {montoContratoNeto > 0 ? formatearCLP(montoContratoNeto) : '$0'}
                  </small>
                </div>

                <div className="form-group">
                  <label htmlFor="p_monto_bruto">Monto Contrato Bruto (IVA 19%)</label>
                  <input
                    id="p_monto_bruto"
                    type="number"
                    min={0}
                    step={1000}
                    value={montoContratoBruto || ''}
                    onChange={(e) => manejarCambioBruto(Number(e.target.value))}
                    placeholder="Ej: 29750000"
                  />
                  <small style={{ fontSize: 10.5, color: 'var(--text-muted)' }}>
                    {montoContratoBruto > 0 ? formatearCLP(montoContratoBruto) : '$0'}
                  </small>
                </div>

                <div className="form-group">
                  <label htmlFor="p_presupuesto_costo">Presupuesto Costos Neto (CLP)</label>
                  <input
                    id="p_presupuesto_costo"
                    type="number"
                    min={0}
                    step={1000}
                    value={presupuestoCostoNeto || ''}
                    onChange={(e) => setPresupuestoCostoNeto(Number(e.target.value))}
                    placeholder="Ej: 17500000"
                  />
                  <small style={{ fontSize: 10.5, color: 'var(--text-muted)' }}>
                    Techo de costo directo planificado
                  </small>
                </div>
              </div>

              {/* Resumen de Margen Planificado */}
              {montoContratoNeto > 0 && (
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginTop: 12,
                    paddingTop: 10,
                    borderTop: '1px solid var(--border-subtle)',
                    fontSize: 12,
                  }}
                >
                  <span style={{ color: 'var(--text-muted)' }}>
                    Margen proyectado original:
                  </span>
                  <strong
                    style={{
                      fontFamily: 'var(--font-mono)',
                      color: margenEstimadoPct >= 20 ? 'var(--accent-green)' : margenEstimadoPct >= 10 ? 'var(--accent-yellow)' : 'var(--accent-red)',
                    }}
                  >
                    {formatearCLP(margenEstimadoNeto)} ({margenEstimadoPct}%)
                  </strong>
                </div>
              )}
            </div>

            {/* 3. Fechas y Plazos */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 16 }}>
              <div className="form-group">
                <label htmlFor="p_fecha_inicio">Fecha de Inicio *</label>
                <input
                  id="p_fecha_inicio"
                  type="date"
                  value={fechaInicio}
                  onChange={(e) => setFechaInicio(e.target.value)}
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="p_fecha_fin">Fecha Fin Planificada</label>
                <input
                  id="p_fecha_fin"
                  type="date"
                  value={fechaFinPlanificada}
                  onChange={(e) => setFechaFinPlanificada(e.target.value)}
                  min={fechaInicio}
                />
                <small style={{ fontSize: 10.5, color: 'var(--text-muted)' }}>
                  Plazo contractual para monitorear atrasos
                </small>
              </div>

              <div className="form-group">
                <label htmlFor="p_facturacion">Modalidad de Facturación</label>
                <select
                  id="p_facturacion"
                  value={modoFacturacion}
                  onChange={(e) => setModoFacturacion(e.target.value as ModoFacturacion)}
                >
                  <option value="AL_CIERRE">Al Cierre (1 sola cuota al entregar)</option>
                  <option value="POR_HITOS">Por Hitos (contra recepción de entregables)</option>
                  <option value="MENSUAL_FIJO">Mensual Fijo (cuotas periódicas continuas)</option>
                  <option value="PORCENTAJES">Porcentajes (anticipo y estados de pago)</option>
                </select>
              </div>
            </div>

            {/* 4. Orden de Compra y Descripción */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 16 }}>
              <div className="form-group">
                <label htmlFor="p_oc_numero">Número de Orden de Compra (OC)</label>
                <input
                  id="p_oc_numero"
                  type="text"
                  value={numeroOrdenCompra}
                  onChange={(e) => setNumeroOrdenCompra(e.target.value)}
                  placeholder="Ej: 1234-567-SE26"
                />
              </div>

              <div className="form-group">
                <label htmlFor="p_oc_fecha">Fecha de Emisión OC</label>
                <input
                  id="p_oc_fecha"
                  type="date"
                  value={fechaOrdenCompra}
                  onChange={(e) => setFechaOrdenCompra(e.target.value)}
                />
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="p_desc">Descripción o Alcance del Proyecto</label>
              <textarea
                id="p_desc"
                rows={2}
                value={descripcion}
                onChange={(e) => setDescripcion(e.target.value)}
                placeholder="Breve descripción del alcance del contrato, entregables y especificaciones técnicas…"
              />
            </div>

            <div className="form-group">
              <label htmlFor="p_notas">Notas Internas</label>
              <textarea
                id="p_notas"
                rows={2}
                value={notas}
                onChange={(e) => setNotas(e.target.value)}
                placeholder="Comentarios o acuerdos del equipo comercial/operativo…"
              />
            </div>
          </div>

          <div className="modal-footer" style={{ borderTop: '1px solid var(--border-subtle)', padding: '14px 20px', display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
            <button
              type="button"
              className="btn btn-secondary"
              onClick={alCerrar}
              disabled={crearMut.isPending}
            >
              Cancelar
            </button>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={crearMut.isPending}
              style={{ fontWeight: 700 }}
            >
              {crearMut.isPending ? 'Creando proyecto…' : 'Crear Proyecto'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
